import { CSSProperties } from "react";
import { isNotNil } from "./utils";
import {
	FontFamily,
	FontSize,
	FontStyleModel
} from "client/Api/common";

export type FontSizeName =
	| "xx-small"
	| "x-small"
	| "small"
	| "medium"
	| "large"
	| "x-large"
	| "xx-large"
	| "xxx-large";

export type FontFamilyDefinition = {
	title: string;
	textRepresentation: string;
	parseRegExp: RegExp;
};

export const fontFamilyDefinition: {[key in FontFamily]: FontFamilyDefinition} = {
	[FontFamily.Arial]: {
		title: "Arial",
		textRepresentation: "Arial, Helvetica, sans-serif",
		parseRegExp: /^Arial(?=,|$)/i
	},
	[FontFamily.CourierNew]: {
		title: "Courier New",
		textRepresentation: `"Courier New", Courier, monospace`,
		parseRegExp: /"Courier New"(?=,|$)/i
	},
	[FontFamily.Georgia]: {
		title: "Georgia",
		textRepresentation: "Georgia, serif",
		parseRegExp: /Georgia(?=,|$)/i
	},
	[FontFamily.Impact]: {
		title: "Impact",
		textRepresentation: "Impact, Charcoal, sans-serif",
		parseRegExp: /Impact(?=,|$)/i
	},
	[FontFamily.LucidaConsole]: {
		title: "Lucida Console",
		textRepresentation: `"Lucida Console", Monaco, monospace`,
		parseRegExp: /"Lucida Console"(?=,|$)/i
	},
	[FontFamily.Tahoma]: {
		title: "Tahoma",
		textRepresentation: "Tahoma, Geneva, sans-serif",
		parseRegExp: /Tahoma(?=,|$)/i
	},
	[FontFamily.TimesNewRoman]: {
		title: "Times New Roman",
		textRepresentation: `"Times New Roman", Times,serif`,
		parseRegExp: /"Times New Roman"(?=,|$)/i
	},
	[FontFamily.TrebuchetMs]: {
		title: "Trebuchet MS",
		textRepresentation: `"Trebuchet MS", Helvetica, sans-serif`,
		parseRegExp: /"Trebuchet MS"(?=,|$)/i
	},
	[FontFamily.Verdana]: {
		title: "Verdana",
		textRepresentation: "Verdana, Geneva, sans-serif",
		parseRegExp: /Verdana(?=,|$)/i
	}
};

export const supportedFontFamilies: readonly FontFamily[] = Object.keys(fontFamilyDefinition)
	.map(v => parseInt(v, 10) as FontFamily);

export type FontSizeDefinition = {
	name: FontSizeName;
	title: string;
};

export const fontSizeDefinition: {[key in FontSize]: FontSizeDefinition} = {
	[FontSize.ExtraExtraSmall]: {
		name: "xx-small",
		title: "1 (8pt)"
	},
	[FontSize.ExtraSmall]: {
		name: "x-small",
		title: "2 (10pt)"
	},
	[FontSize.Small]: {
		name: "small",
		title: "3 (12pt)"
	},
	[FontSize.Medium]: {
		name: "medium",
		title: "4 (14pt)"
	},
	[FontSize.Large]: {
		name: "large",
		title: "5 (18pt)"
	},
	[FontSize.ExtraLarge]: {
		name: "x-large",
		title: "6 (24pt)"
	},
	[FontSize.ExtraExtraLarge]: {
		name: "xx-large",
		title: "7 (36pt)"
	},
	[FontSize.ExtraExtraExtraLarge]: {
		name: "xxx-large",
		title: "8 (54pt)"
	}
};

export const supportedFontSizes: readonly FontSize[] = Object.keys(fontSizeDefinition)
	.map(v => parseInt(v, 10) as FontSize);

export function convertToCss(fontStyleModel: FontStyleModel): CSSProperties {
	const result: CSSProperties = {};

	if (isNotNil(fontStyleModel.family)) {
		result.fontFamily = fontFamilyDefinition[fontStyleModel.family]
			.textRepresentation;
	}

	if (isNotNil(fontStyleModel.size)) {
		result.fontSize = fontSizeDefinition[fontStyleModel.size]
			.name;
	}

	if (fontStyleModel.bold) {
		result.fontWeight = "bold";
	}

	if (fontStyleModel.italic) {
		result.fontStyle = "italic";
	}

	if (fontStyleModel.underline) {
		result.textDecoration = "underline";
	}

	if (isNotNil(fontStyleModel.color)) {
		result.color = fontStyleModel.color;
	}

	return result;
}